import { Fragment, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';

import Modal from '../../common/Modal';
import ModalButton from '../../common/ModalButton';
import { SONG_SORT_OPTIONS } from '../../../helpers/songFilters';
import { replaceAccents } from '../../../utils/string';

import './SongFiltersModal.scss';

const VISIBLE_SERVICE_NAMES = 8;

const SongFiltersModal = ({
  isOpen,
  onClose,
  sort,
  onSortChange,
  keyOptions,
  selectedKeys,
  onKeysChange,
  timeSignatureOptions,
  selectedTimeSignatures,
  onTimeSignaturesChange,
  themeOptions,
  selectedThemes,
  onThemesChange,
  chartOptions,
  selectedCharts,
  onChartsChange,
  serviceNames,
  excludedServiceNames,
  onExcludedServiceNamesChange,
  onReset,
  resultCount,
  accentColor,
}) => {
  const { t } = useTranslation();
  const [showAllServices, setShowAllServices] = useState(false);
  const [openPanels, setOpenPanels] = useState([]);
  const [themeSearch, setThemeSearch] = useState('');
  // The results' height when the search began, held so narrowing them doesn't shrink the modal
  const [themeResultsHeight, setThemeResultsHeight] = useState(null);
  const themeResultsRef = useRef(null);

  // The filters under Filter by, each a chip that opens its own panel of count chips
  const panels = [
    {
      id: 'key',
      label: t('filters.key'),
      rows: [keyOptions.major, keyOptions.minor],
      picked: selectedKeys,
      onChange: onKeysChange,
    },
    {
      id: 'time-signature',
      label: t('filters.timeSignature'),
      rows: [timeSignatureOptions],
      picked: selectedTimeSignatures,
      onChange: onTimeSignaturesChange,
    },
    themeOptions.length > 0 && {
      id: 'themes',
      label: t('filters.themes'),
      picked: selectedThemes,
      onChange: onThemesChange,
    },
    {
      id: 'charts',
      label: t('filters.charts'),
      picked: selectedCharts,
      onChange: onChartsChange,
    },
  ].filter(Boolean);
  // Kept open while something in it is picked, so the pick stays in sight
  const isPanelOpen = (panel) => openPanels.includes(panel.id) || panel.picked.length > 0;

  // The service filter only applies to the Newest/Oldest sorts, so it's hidden and ignored otherwise
  const showServices = sort === 'newest' || sort === 'oldest';
  const hasActiveFilters =
    sort !== 'none' ||
    selectedKeys.length > 0 ||
    selectedTimeSignatures.length > 0 ||
    selectedThemes.length > 0 ||
    selectedCharts.length > 0 ||
    (showServices && excludedServiceNames.length > 0);
  const tickedServiceCount = serviceNames.length - excludedServiceNames.length;
  // Kept open when an unticked name sits past the cut, so nothing unticked is ever out of sight
  const servicesExpanded =
    showAllServices || serviceNames.slice(VISIBLE_SERVICE_NAMES).some((name) => excludedServiceNames.includes(name.id));
  const visibleServiceNames = servicesExpanded ? serviceNames : serviceNames.slice(0, VISIBLE_SERVICE_NAMES);

  const toggleIn = (list, value) => (list.includes(value) ? list.filter((item) => item !== value) : [...list, value]);

  // Closing a panel drops its picks, so nothing filters the list from out of sight
  const togglePanel = (panel) => {
    if (isPanelOpen(panel)) {
      panel.onChange([]);
      if (panel.id === 'themes') handleThemeSearch('');
      setOpenPanels(openPanels.filter((id) => id !== panel.id));
    } else {
      setOpenPanels([...openPanels, panel.id]);
    }
  };

  const toggleServiceName = (id) => {
    if (excludedServiceNames.includes(id)) {
      onExcludedServiceNamesChange(excludedServiceNames.filter((name) => name !== id));
    } else if (tickedServiceCount > 1) {
      onExcludedServiceNamesChange([...excludedServiceNames, id]);
    }
  };

  // Always laid out and only hidden, so the header keeps its height when the action comes and goes
  const renderSectionAction = (label, visible, onClick) => (
    <button
      type="button"
      className={cx('section-action', { 'section-action--hidden': !visible })}
      onClick={onClick}
      disabled={!visible}
      aria-hidden={!visible}
    >
      {label}
    </button>
  );

  // Chips that name a value and count the songs in it, for each panel under Filter by
  const renderCountChips = (options, picked, onChange) => (
    <div className="key-grid">
      {options.map((option) => {
        const selected = picked.includes(option.value);

        return (
          <button
            key={option.value}
            type="button"
            className={cx('chip', 'key-chip', { 'chip--selected': selected })}
            aria-pressed={selected}
            disabled={option.count === 0 && !selected}
            onClick={() => onChange(toggleIn(picked, option.value))}
          >
            <span className="key-name">{option.label}</span>
            <span className="key-count">{option.count}</span>
          </button>
        );
      })}
    </div>
  );

  // Chips whose labels are words, wrapped rather than laid on the key grid
  const renderWordChips = (options, picked, onChange) => (
    <div className="chips">
      {options.map((option) => {
        const selected = picked.includes(option.value);

        return (
          <button
            key={option.value}
            type="button"
            className={cx('chip', 'theme-chip', { 'chip--selected': selected })}
            aria-pressed={selected}
            disabled={option.count === 0 && !selected}
            onClick={() => onChange(toggleIn(picked, option.value))}
          >
            {option.label}
            <span className="theme-count">{option.count}</span>
          </button>
        );
      })}
    </div>
  );

  // Themes are open-ended, so they get a search and wrap as words; a theme no song in view carries
  // is left out unless it's picked
  const handleThemeSearch = (value) => {
    if (!value) setThemeResultsHeight(null);
    else if (!themeSearch) setThemeResultsHeight(themeResultsRef.current?.offsetHeight ?? null);
    setThemeSearch(value);
  };

  const renderThemes = () => {
    const query = replaceAccents(themeSearch.trim().toLowerCase());
    const visible = themeOptions.filter(
      (option) =>
        (option.count > 0 || selectedThemes.includes(option.value)) &&
        replaceAccents(option.label.toLowerCase()).includes(query),
    );

    return (
      <>
        <input
          type="search"
          className="theme-search"
          placeholder={t('filters.searchThemes')}
          value={themeSearch}
          onChange={(e) => handleThemeSearch(e.target.value)}
          aria-label={t('filters.searchThemes')}
        />
        <div ref={themeResultsRef} style={themeResultsHeight ? { minHeight: themeResultsHeight } : undefined}>
          {visible.length > 0 ? (
            renderWordChips(visible, selectedThemes, onThemesChange)
          ) : (
            <p className="empty">{t('filters.noThemes')}</p>
          )}
        </div>
      </>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('filters.title')}
      className="SongFiltersModal"
      footer={
        <>
          <ModalButton variant="secondary" className="reset" onClick={onReset} disabled={!hasActiveFilters}>
            {t('common.reset')}
          </ModalButton>
          <ModalButton onClick={onClose}>{t('filters.show', { count: resultCount })}</ModalButton>
        </>
      }
    >
      <div className="filters" style={accentColor ? { '--tag-color': accentColor } : undefined}>
        <section className="section">
          <div className="section-header">
            <h4 className="section-title">{t('filters.sortBy')}</h4>
          </div>
          <div className="chips" role="radiogroup" aria-label={t('filters.sortBy')}>
            {SONG_SORT_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={sort === option.value}
                className={cx('chip', { 'chip--selected': sort === option.value })}
                onClick={() => onSortChange(option.value)}
              >
                {t(`filters.sort.${option.value}`)}
              </button>
            ))}
          </div>
        </section>

        {serviceNames.length > 0 && (
          <div className={cx('reveal', { 'reveal--open': showServices })} aria-hidden={!showServices}>
            <div className="reveal-inner">
              <section className="section">
                <div className="section-header">
                  <h4 className="section-title">{t('filters.filterByService')}</h4>
                  {renderSectionAction(t('common.all'), excludedServiceNames.length > 0, () =>
                    onExcludedServiceNamesChange([]),
                  )}
                </div>
                <div className="chips">
                  {visibleServiceNames.map((name) => {
                    const ticked = !excludedServiceNames.includes(name.id);

                    return (
                      <button
                        key={name.id}
                        type="button"
                        className={cx('chip', { 'chip--selected': ticked })}
                        aria-pressed={ticked}
                        onClick={() => toggleServiceName(name.id)}
                      >
                        {name.label}
                      </button>
                    );
                  })}
                  {!servicesExpanded && serviceNames.length > VISIBLE_SERVICE_NAMES && (
                    <button type="button" className="chip chip--more" onClick={() => setShowAllServices(true)}>
                      {t('filters.showAll', { count: serviceNames.length })}
                    </button>
                  )}
                </div>
              </section>
            </div>
          </div>
        )}

        <section className="section">
          <div className="section-header">
            <h4 className="section-title">{t('filters.filterBy')}</h4>
          </div>
          <div className="chips">
            {panels.map((panel) => (
              <button
                key={panel.id}
                type="button"
                className={cx('chip', { 'chip--selected': isPanelOpen(panel) })}
                aria-pressed={isPanelOpen(panel)}
                aria-expanded={isPanelOpen(panel)}
                onClick={() => togglePanel(panel)}
              >
                {panel.label}
              </button>
            ))}
          </div>
        </section>

        {panels.map((panel) => (
          <div
            key={panel.id}
            className={cx('reveal', { 'reveal--open': isPanelOpen(panel) })}
            aria-hidden={!isPanelOpen(panel)}
          >
            <div className="reveal-inner">
              <section className="section">
                <div className="section-header">
                  <h4 className="section-title">{panel.label}</h4>
                  {renderSectionAction(t('common.clear'), panel.picked.length > 0, () => panel.onChange([]))}
                </div>
                {panel.id === 'themes' && renderThemes()}
                {panel.id === 'charts' &&
                  renderWordChips(
                    chartOptions.map((option) => ({ ...option, label: t(`filters.chart.${option.value}`) })),
                    selectedCharts,
                    onChartsChange,
                  )}
                {panel.rows &&
                  panel.rows.map((options, index) => (
                    <Fragment key={index}>{renderCountChips(options, panel.picked, panel.onChange)}</Fragment>
                  ))}
              </section>
            </div>
          </div>
        ))}
      </div>
    </Modal>
  );
};

SongFiltersModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  sort: PropTypes.string.isRequired,
  onSortChange: PropTypes.func.isRequired,
  // From getKeyFilterOptions
  keyOptions: PropTypes.shape({ major: PropTypes.array, minor: PropTypes.array }).isRequired,
  selectedKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
  onKeysChange: PropTypes.func.isRequired,
  // From getTimeSignatureFilterOptions
  timeSignatureOptions: PropTypes.arrayOf(
    PropTypes.shape({ value: PropTypes.string, label: PropTypes.string, count: PropTypes.number }),
  ).isRequired,
  selectedTimeSignatures: PropTypes.arrayOf(PropTypes.string).isRequired,
  onTimeSignaturesChange: PropTypes.func.isRequired,
  // From getThemeFilterOptions
  themeOptions: PropTypes.arrayOf(
    PropTypes.shape({ value: PropTypes.string, label: PropTypes.string, count: PropTypes.number }),
  ).isRequired,
  selectedThemes: PropTypes.arrayOf(PropTypes.string).isRequired,
  onThemesChange: PropTypes.func.isRequired,
  // From getChartFilterOptions
  chartOptions: PropTypes.arrayOf(
    PropTypes.shape({ value: PropTypes.string, label: PropTypes.string, count: PropTypes.number }),
  ).isRequired,
  selectedCharts: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChartsChange: PropTypes.func.isRequired,
  // From getServiceNameOptions
  serviceNames: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, label: PropTypes.string })).isRequired,
  excludedServiceNames: PropTypes.arrayOf(PropTypes.string).isRequired,
  onExcludedServiceNamesChange: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  resultCount: PropTypes.number.isRequired,
  accentColor: PropTypes.string,
};

export default SongFiltersModal;
